import { Link } from "react-router-dom";
import { LogoMark } from "@/components/app/Brand";
import { ArrowLeft, ArrowRight, Mail, MessageSquare, BookOpen, LifeBuoy } from "lucide-react";

const FAQS = [
  { q: "How do I get started?", a: "Create a free account and you'll be up and running in a couple of minutes. See the \"How it works\" section on our homepage for a walkthrough." },
  { q: "Can I switch plans later?", a: "Yes. You can upgrade, downgrade, or cancel at any time from your account billing settings, and changes take effect immediately." },
  { q: "Do you offer a free trial on paid plans?", a: "Pro includes a 7-day free trial and Business a 14-day free trial. A card is required to start a trial, but you won't be charged until it ends." },
  { q: "Where can I manage support tickets once I've signed up?", a: "Signed-in users have a full Help Center with searchable articles and a support ticket queue, available from the sidebar once you're logged in." },
];

export default function PublicHelpCenter() {
  return (
    <div className="bg-white text-gray-900 min-h-screen font-sans">
      <nav className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-violet-600 grid place-items-center shadow-md shadow-violet-200">
              <LogoMark size={16} color="#fff" />
            </div>
            <span className="text-base font-bold text-gray-900 tracking-tight">Combined<span className="text-violet-600">Work</span></span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
        </div>
      </nav>

      <header className="max-w-3xl mx-auto px-6 pt-20 pb-12 text-center">
        <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full mb-4">
          <LifeBuoy className="w-3.5 h-3.5" /> Help Center
        </span>
        <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tight text-gray-900 mb-4">
          How can we help?
        </h1>
        <p className="text-gray-500 text-lg max-w-xl mx-auto">
          Answers to common questions, plus how to reach us directly.
        </p>
      </header>

      <section className="max-w-3xl mx-auto px-6 py-8">
        <h2 className="font-display font-bold text-xl text-gray-900 mb-6 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-violet-500" /> Frequently asked questions
        </h2>
        <div className="space-y-4 mb-16">
          {FAQS.map((f) => (
            <div key={f.q} className="border border-gray-100 rounded-2xl p-5">
              <h3 className="font-semibold text-gray-800 text-sm mb-1.5">{f.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="border border-gray-100 rounded-2xl p-6">
            <div className="w-10 h-10 rounded-xl bg-violet-50 grid place-items-center mb-4">
              <Mail className="w-5 h-5 text-violet-600" strokeWidth={1.75} />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm mb-2">Contact us</h3>
            <p className="text-gray-500 text-xs leading-relaxed mb-4">
              Can't find what you need? Email us and we'll get back to you within one business day.
            </p>
            <a href="mailto:hello@combinedwork.com" className="text-sm font-semibold text-violet-600 hover:text-violet-700">
              hello@combinedwork.com
            </a>
          </div>
          <div className="border border-gray-100 rounded-2xl p-6">
            <div className="w-10 h-10 rounded-xl bg-violet-50 grid place-items-center mb-4">
              <MessageSquare className="w-5 h-5 text-violet-600" strokeWidth={1.75} />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm mb-2">Already a customer?</h3>
            <p className="text-gray-500 text-xs leading-relaxed mb-4">
              Sign in to search the full knowledge base and open a support ticket with our team.
            </p>
            <Link to="/auth" className="inline-flex items-center gap-1 text-sm font-semibold text-violet-600 hover:text-violet-700">
              Sign in <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-100 py-8 px-6 text-center">
        <span className="text-xs text-gray-400">&copy; {new Date().getFullYear()} CombinedWork. All rights reserved.</span>
      </footer>
    </div>
  );
}
